import { leadSentences } from "./data/preview-text";
import { type WatchLeagueState } from "@/lib/leagues/watch/index";
import { type StorePageContent } from "@/features/store-editor/records";
import { previewCopy } from "./copy/preview-copy";
import {
  type PreviewLadder,
  type PreviewStanding,
  type PreviewRewards,
  type LeagueRules,
} from "./content/preview-competition.types";
import {
  aboutFrom,
  joinSentences,
  previewLead,
} from "./content/preview-summary";
import { applyStorePageOverride } from "@/features/preview/content/preview-content";
import { firstVariantFacts } from "./metadata/manifest-facts";
import { type CoworldSource } from "./metadata/coworld-source";
import { type ResolvedPreviewLeague } from "./preview-league.data";
export function previewStory({
  preview,
  state,
  manifest,
  source,
  scoreLabel,
  rules,
  rewards,
  content,
}: {
  readonly preview: ResolvedPreviewLeague;
  readonly state: WatchLeagueState | null;
  readonly manifest: unknown;
  readonly source: CoworldSource | null;
  readonly scoreLabel: string | null;
  readonly rules: LeagueRules;
  readonly rewards: PreviewRewards | null;
  readonly content: StorePageContent | null;
}) {
  const {
    league,
    route,
    leagueSegment,
    coworldSlug,
    leagueCount,
    coworldName,
    name,
  } = preview;
  const dailyAward = rewards !== null && rewards.nextAward !== null;
  const { brief, pitch, opening } = previewLead({
    league,
    leagueSegment,
    coworldName,
  });
  const { maxTicks, maxTurns, numAgents } = firstVariantFacts(manifest);
  const templateCopyResult = previewCopy({
    game: route.game,
    slug: leagueSegment ? `${coworldSlug}/${leagueSegment}` : coworldSlug,
    name,
    leagueCount: leagueSegment ? 1 : leagueCount,
    playerCount: state?.playerCount ?? null,
    divisionName: state?.divisionName ?? null,
    brief,
    roundMinutes: rules.roundMinutes,
    seats: rules.seats,
    scoreLabel,
    scoring: rules.scoring,
    minEpisodes: rules.minEpisodes,
    dailyAward,
    maxTicks,
    maxTurns,
    numAgents,
    playerTypes: source?.playerTypes ?? null,
  });
  const templateBlurb =
    templateCopyResult.blurb ??
    ((brief && opening
      ? leadSentences({
          text: joinSentences({
            opening,
            rest: brief.rest,
          }),
        })
      : "") ||
      pitch ||
      "");
  const supplementaryPitch = pitch && pitch !== templateBlurb ? pitch : null;
  const templateClosingLine = templateCopyResult.blurb
    ? templateCopyResult.closingLine
    : supplementaryPitch;
  const { blurb, closingLine, copy } = applyStorePageOverride({
    blurb: templateBlurb,
    closingLine: templateClosingLine,
    copy: templateCopyResult,
    content,
  });
  const about = aboutFrom({
    curatedBlurb: templateCopyResult.blurb,
    brief,
    opening,
    blurb,
  });
  const team =
    copy.team ??
    (source?.repoUrl && source.repo
      ? { title: "Makers", subtitle: source.repo, href: source.repoUrl }
      : null);
  return { blurb, closingLine, copy, about, team };
}
export function previewLadder({
  state,
  leader,
  rules,
}: {
  readonly state: WatchLeagueState | null;
  readonly leader: PreviewStanding | null;
  readonly rules: LeagueRules;
}): PreviewLadder | null {
  if (!leader) return null;

  const reign = state?.firstPlace?.current ?? null;
  const mine = reign !== null && reign.player_id === leader.playerId;
  return {
    recentRounds: [],
    scoring: rules.scoring,
    pairing: rules.pairing,
    minEpisodes: rules.minEpisodes,
    roundMinutes: rules.roundMinutes,
    reignSince: mine ? reign.started_at : null,
    reignRounds: mine ? reign.rounds_held : null,
    lastRoundAt: state?.newestCompletedAt ?? null,
  };
}
